/**
 * availability-view — shared DOM updates driven by a variant's live availability
 * (`available` + `estimatedShipTime`). Used by both the full <pf-variant-picker>
 * and the standalone <pf-availability> so the two render availability identically.
 */

/**
 * @param {string|null|undefined} available
 * @returns {boolean}
 */
export function isPurchasable(available) {
  return available == null || available === 'instock';
}

/**
 * Populate the estimated-ship-time field within `root` from the variant. The API
 * pre-formats the string ("Ships in 1-2 business days") — render it as-is. Hides
 * the field when the variant has no ship time.
 * @param {ParentNode|null} root
 * @param {{estimatedShipTime?: string|null}|null} variant
 */
export function updateShipTime(root, variant) {
  fillSlot(root, '[data-pf-ship-time]', variant?.estimatedShipTime);
}

const AVAILABILITY_STRINGS = {
  backorder: 'backordered',
  oos: 'outOfStock',
};

/**
 * @param {string|null|undefined} available
 * @returns {string|undefined}
 */
export function availabilityString(available) {
  const key = AVAILABILITY_STRINGS[available];
  return key && globalThis.theme?.strings?.[key];
}

/**
 * @param {ParentNode|null} root
 * @param {{available?: string|null}|null} variant
 */
export function updateAvailabilityLabel(root, variant) {
  fillSlot(
    root,
    '[data-pf-availability-label]',
    availabilityString(variant?.available)
  );
}

function fillSlot(root, selector, text) {
  const el = root?.querySelector(selector);
  if (!el) return;
  el.textContent = text || '';
  el.hidden = !text;
}

/**
 * Reflect availability on the add-to-cart button within `form`.
 *
 * @param {ParentNode|null} form  the product form (.js-product-form)
 * @param {{productAvailable: boolean, variant: {id?: *, available?: string|null}|null}} state
 */
export function updateAddToCartButton(form, { productAvailable, variant }) {
  if (!form) return;
  const btn = form.querySelector('[name="add"]');
  if (!btn) return;

  const strings = globalThis.theme?.strings;
  if (!variant || variant.id == null) {
    btn.disabled = true;
    btn.textContent = strings?.noVariant ?? btn.textContent;
    return;
  }
  const purchasable =
    variant.available == null
      ? productAvailable
      : isPurchasable(variant.available);
  btn.disabled = !purchasable;
  btn.textContent = purchasable
    ? btn.dataset.addToCartText
    : (strings?.noStock ?? btn.textContent);
}
